/**
 * The arithmetic behind the Area Scoring dashboard.
 *
 * WHAT THE COMPOSITE ACTUALLY IS
 *
 * `compositeScore` on the server
 * (`backend/src/modules/area-intelligence/domain/scoring/composite.js`) is a
 * weighted mean over configured components, normalised by the weight of the
 * components it could actually find:
 *
 *     score      = SUM(score_i * weight_i) / SUM(weight_i)
 *     confidence = components found / components configured
 *
 * Two consequences worth stating, because both are easy to get wrong:
 *
 *   1. The weights live on the COMPOSITE dimension's own configuration, not
 *      on each component dimension's `composite_weight` column. Reading the
 *      column would display influence the composite does not use.
 *   2. A missing component does not drag the score down — it is excluded and
 *      the confidence falls instead. So a 9.1 built from two of six
 *      components is not a high score, it is a provisional one, and a page
 *      that showed the 9.1 without the 2-of-6 would be misleading.
 *
 * WHY CONTRIBUTIONS, AND NOT SIX EQUAL DIALS
 *
 * Because the weighted mean decomposes exactly. Define
 *
 *     contribution_i = score_i * weight_i / SUM(weight_i)
 *
 * and the contributions sum to the composite. That is what lets the dashboard
 * draw the composite as one box whose filled proportion IS the score: a
 * statement about the model, not a visual metaphor for it. A grid of equal
 * dials, which is what the existing area page draws, actively hides the thing
 * an agent needs — that a 9/10 at weight 0.05 matters less than a 6/10 at
 * weight 0.30.
 */
import type {
  AreaScoreCalculation,
  AreaScoreCard,
  WeightedComponent,
} from '@/types/areaIntelligence'

/** The dimension slug the engine treats as the headline composite. */
export const COMPOSITE_SLUG = 'overall_livability'

/** Scores are 0-10 throughout the platform. Not rescaled here: one scale. */
export const SCORE_MAX = 10

export interface Contribution {
  slug: string
  label: string
  score: number
  weight: number
  /** Share of total influence, 0-1. Widths in the box are drawn from this. */
  share: number
  /** score * share. These sum to the composite. */
  contribution: number
  /** Per-dimension confidence, 0-1, from that dimension's own calculation. */
  confidence: number | null
  rationale: string | null
}

export interface CompositeView {
  /** The server's number, untouched. Null when it could not be computed. */
  platformScore: number | null
  /** Components found over components configured, 0-1. */
  coverage: number | null
  contributions: Contribution[]
  /** Configured components the engine could not find a score for. */
  missing: string[]
}

function labelFor(slug: string, cards: AreaScoreCard[]): string {
  const card = cards.find((c) => c.dimension.slug === slug)
  if (card) return card.dimension.name
  // A component configured against a slug with no active dimension. Showing
  // the raw slug is better than hiding it: it is a configuration fault
  // someone needs to see.
  return slug.replace(/_/g, ' ')
}

/**
 * Join the two endpoints into the breakdown the dashboard draws.
 *
 * Reads the composite's recorded `weightedComponents` rather than recomputing
 * from the dimension list, so the page shows the weights the score was really
 * built from even if the configuration has changed since.
 */
export function buildCompositeView(
  cards: AreaScoreCard[],
  calculations: AreaScoreCalculation[],
): CompositeView {
  const compositeCard = cards.find((c) => c.dimension.slug === COMPOSITE_SLUG)
  const compositeCalc = calculations.find(
    (calc) => compositeCard && calc.dimension_id === compositeCard.dimension.id,
  )

  const components: WeightedComponent[] = Array.isArray(
    compositeCalc?.input_formula?.weightedComponents,
  )
    ? (compositeCalc!.input_formula!.weightedComponents as WeightedComponent[])
    : []

  const totalWeight = components.reduce(
    (sum, component) => sum + (Number.isFinite(component.weight) ? component.weight : 0),
    0,
  )

  const byDimensionId = new Map(calculations.map((calc) => [calc.dimension_id, calc]))

  const contributions: Contribution[] = components.map((component) => {
    const share = totalWeight > 0 ? component.weight / totalWeight : 0
    const card = cards.find((c) => c.dimension.slug === component.dimension_slug)
    const calc = card ? byDimensionId.get(card.dimension.id) : undefined
    return {
      slug: component.dimension_slug,
      label: labelFor(component.dimension_slug, cards),
      score: component.score,
      weight: component.weight,
      share,
      contribution: component.score * share,
      confidence: calc?.confidence ?? card?.confidence ?? null,
      rationale: calc?.score_rationale ?? card?.rationale ?? null,
    }
  })

  return {
    platformScore: compositeCard?.score ?? null,
    coverage: compositeCalc?.confidence ?? compositeCard?.confidence ?? null,
    contributions,
    missing: Array.isArray(compositeCalc?.input_formula?.missing)
      ? (compositeCalc!.input_formula!.missing as string[])
      : [],
  }
}

/**
 * Recompute the composite under a different set of weights.
 *
 * The same formula the server uses, deliberately: this is the page's one
 * interactive affordance, and it is only defensible because it re-weights the
 * platform's own scores rather than inventing new ones. A buyer who cares
 * twice as much about schools gets an answer built from the same evidence,
 * and the platform score stays on screen beside it so the two are never
 * confused.
 *
 * Returns null when nothing carries weight, rather than a confident zero.
 */
export function recomputeComposite(
  contributions: Contribution[],
  weights: Record<string, number>,
): number | null {
  let weightedSum = 0
  let totalWeight = 0
  for (const entry of contributions) {
    const weight = Number(weights[entry.slug])
    if (!Number.isFinite(weight) || weight <= 0) continue
    if (!Number.isFinite(entry.score)) continue
    weightedSum += entry.score * weight
    totalWeight += weight
  }
  if (totalWeight === 0) return null
  // Rounded the way the server rounds, so the two are directly comparable.
  return Math.round((weightedSum / totalWeight) * 10) / 10
}

/** Contributions under a custom weighting, for redrawing the box. */
export function reweight(
  contributions: Contribution[],
  weights: Record<string, number>,
): Contribution[] {
  const total = contributions.reduce((sum, entry) => {
    const weight = Number(weights[entry.slug])
    return sum + (Number.isFinite(weight) && weight > 0 ? weight : 0)
  }, 0)
  return contributions.map((entry) => {
    const weight = Number(weights[entry.slug])
    const safe = Number.isFinite(weight) && weight > 0 ? weight : 0
    const share = total > 0 ? safe / total : 0
    return { ...entry, weight: safe, share, contribution: entry.score * share }
  })
}

export function defaultWeights(contributions: Contribution[]): Record<string, number> {
  return Object.fromEntries(contributions.map((entry) => [entry.slug, entry.weight]))
}

/**
 * How a score should be described out loud.
 *
 * Deliberately coarse. A dashboard that distinguishes 7.1 from 7.3 in words
 * implies the model separates them, and over a handful of signals it does not.
 */
export function bandFor(score: number | null): string {
  if (score == null || !Number.isFinite(score)) return 'Not scored'
  if (score >= 8) return 'Strong'
  if (score >= 6.5) return 'Good'
  if (score >= 5) return 'Mixed'
  return 'Weak'
}

/**
 * Whether a dimension's own score is too thinly evidenced to state plainly.
 *
 * Confidence is 0-1 on the calculation row. Below two thirds the dashboard
 * hatches the segment rather than filling it, which is the same device the
 * pricing instrument uses for a thin comparable pool — one visual grammar for
 * "we are not sure", across both tools.
 */
export const PROVISIONAL_BELOW = 2 / 3

export function isProvisional(confidence: number | null): boolean {
  return confidence != null && Number.isFinite(confidence) && confidence < PROVISIONAL_BELOW
}

export function formatScore(score: number | null): string {
  if (score == null || !Number.isFinite(score)) return '—'
  return score.toFixed(1)
}

export function formatPercent(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${Math.round(value * 100)}%`
}
